// Mock data for Gegadyne Energy Landing Page

export const companyInfo = {
  name: "Gegadyne Energy",
  tagline: "Multi-Ion Battery Cells & Packs",
  logo: "https://customer-assets.emergentagent.com/job_5f3b5187-a50e-4370-8269-aa1aece42742/artifacts/4yye438k_Gegadyne%20Logo%20white%20-%20transparent.png",
  description: "Manufacturing high-performance Multi-Ion battery cells and packs from our Mumbai facility since 2022."
};

export const heroContent = {
  headline: "Multi-Ion Battery Technology",
  subheadline: "Fast Charging. Long Life. Lower Total Cost.",
  description: "We manufacture battery cells and packs that charge in 15 minutes, last 5,000+ cycles, and operate from -40°C to 65°C — without lithium dependency.",
  primaryCta: {
    text: "Talk to Engineering",
    href: "#contact"
  },
  secondaryCta: {
    text: "Download Product Brief",
    href: "#"
  }
};

export const keyMetrics = [
  {
    id: 1,
    value: "5,000+",
    label: "Cycle Life",
    unit: "cycles",
    description: "2.5x longer than lithium-ion"
  },
  {
    id: 2,
    value: "15 min",
    label: "Full Charge",
    unit: "4C rate",
    description: "Safe fast charging at scale"
  },
  {
    id: 3,
    value: "-40° to 65°C",
    label: "Operating Range",
    unit: "",
    description: "Extreme temperature performance"
  }
];

export const validationPartners = [
  { name: "AWS", logo: null },
  { name: "Schneider Electric", logo: null },
  { name: "Siemens", logo: null },
  { name: "Godrej", logo: null },
  { name: "V-Guard", logo: null }
];

export const credibilityStats = [
  { value: "2022", label: "Manufacturing Since" },
  { value: "Mumbai", label: "Production Facility" },
  { value: "50K+", label: "Cells Produced" }
];

export const products = [
  {
    id: "traction",
    name: "Traction Battery",
    tagline: "Industrial Mobility",
    description: "High-performance battery solutions engineered for industrial material handling equipment. Delivering consistent power output across extreme conditions with minimal downtime.",
    features: [
      "15-minute fast charging",
      "5,000+ cycle life",
      "Wide temperature tolerance",
      "Lower total cost of ownership"
    ],
    applications: ["Forklifts", "Material Handling", "Industrial AGVs", "Warehouse Equipment"],
    specs: {
      voltage: "48V - 80V",
      capacity: "100Ah - 500Ah",
      cycleLife: "5,000+ cycles",
      chargeRate: "4C (15 min)"
    },
    image: "/traction-battery.jpg"
  },
  {
    id: "energy-storage",
    name: "Energy Storage Battery",
    tagline: "Reliable Backup & Grid Power",
    description: "Grid-scale and commercial energy storage systems built for reliability, efficiency, and longevity. Perfect for telecom towers, data centers, and industrial backup.",
    features: [
      "High cycle efficiency",
      "Minimal degradation",
      "Wide temperature range",
      "Scalable architecture"
    ],
    applications: ["Telecom Towers", "Data Centers", "UPS Systems", "Grid Storage"],
    specs: {
      voltage: "48V - 400V",
      capacity: "50kWh - 1MWh",
      cycleLife: "5,000+ cycles",
      efficiency: ">95%"
    },
    image: "/energy-storage.jpg"
  },
  {
    id: "starter",
    name: "Starter Battery",
    tagline: "12V Automotive",
    description: "High-performance 12V starter batteries for automotive applications. Superior cranking power in extreme temperatures with extended service life.",
    features: [
      "Extreme cold cranking",
      "3x longer life",
      "Maintenance free",
      "Drop-in replacement"
    ],
    applications: ["Commercial Vehicles", "Fleet Operations", "Off-highway Equipment"],
    specs: {
      voltage: "12V",
      capacity: "60Ah - 200Ah",
      cycleLife: "3,000+ cycles",
      tempRange: "-40°C to 65°C"
    },
    image: "/starter-battery.jpg"
  }
];

export const solutions = [
  {
    id: "material-handling",
    name: "Material Handling",
    icon: "Forklift",
    description: "Forklifts, AGVs, and warehouse equipment",
    problem: "Lead-acid batteries require 8+ hours to charge and frequent replacement. Lithium alternatives are expensive and struggle in cold warehouses.",
    benefits: [
      "15-minute opportunity charging eliminates battery swaps",
      "5,000+ cycles reduces replacement frequency by 60%",
      "Operates reliably in cold storage (-40°C)"
    ],
    metrics: { chargeTime: "15 min", cycleLife: "5,000+", savings: "40% TCO" }
  },
  {
    id: "backup-power",
    name: "Backup Power",
    icon: "Server",
    description: "Telecom, data centers, and UPS systems",
    problem: "VRLA batteries degrade quickly in high temperatures. Traditional solutions have limitations in enclosed spaces.",
    benefits: [
      "Stable performance up to 65°C without cooling",
      "Inherently safe chemistry",
      "10-year operational life reduces maintenance"
    ],
    metrics: { tempRange: "-40° to 65°C", safety: "Inherently stable", life: "10+ years" }
  },
  {
    id: "grid-industrial",
    name: "Industrial & Grid",
    icon: "Factory",
    description: "Peak shaving, load balancing, renewable integration",
    problem: "Grid-scale storage requires high cycle life and predictable degradation. Price volatility of lithium impacts project economics.",
    benefits: [
      "5,000+ cycles with <20% degradation",
      "Stable material costs — no lithium exposure",
      "Scalable from 50kWh to multi-MWh"
    ],
    metrics: { cycles: "5,000+", degradation: "<20%", scale: "50kWh - MWh" }
  },
  {
    id: "starter-batteries",
    name: "Starter Batteries",
    icon: "Car",
    description: "12V automotive and commercial vehicles",
    problem: "Traditional lead-acid batteries fail in extreme temperatures and have short service life in demanding applications.",
    benefits: [
      "Superior cold cranking at -40°C",
      "3x longer service life than lead-acid",
      "Maintenance-free operation"
    ],
    metrics: { coldCrank: "-40°C", life: "3x longer", maintenance: "Zero" }
  }
];

export const technologyHighlights = {
  title: "Multi-Ion Battery Technology",
  subtitle: "How It Works",
  description: "Our proprietary Multi-Ion technology uses potassium ions to create permanent structural pillars in the electrode, enabling sodium ions to flow freely for safe, fast charging.",
  steps: [
    {
      id: 1,
      title: "Ion Movement",
      description: "Larger K+ ions migrate into electrode layers during initial activation, expanding the crystal structure."
    },
    {
      id: 2,
      title: "Charge Behavior",
      description: "K+ ions lock as permanent pillars, creating wide pathways. Na+ ions flow freely through these channels."
    },
    {
      id: 3,
      title: "Performance Outcome",
      description: "Result: 4C safe charging (15 min full charge), 5,000+ cycles, and stable performance across temperatures."
    }
  ],
  comparison: [
    { metric: "Cycle Life", lithium: "~2,000 cycles", multiIon: "5,000+ cycles" },
    { metric: "Charge Time", lithium: "1-2 hours", multiIon: "15 minutes" },
    { metric: "Temperature Range", lithium: "0°C to 45°C", multiIon: "-40°C to 65°C" },
    { metric: "Supply Chain Risk", lithium: "High (concentrated)", multiIon: "Low (abundant materials)" },
    { metric: "Safety Profile", lithium: "Requires monitoring", multiIon: "Inherently stable" }
  ]
};

export const navigationItems = [
  { name: "About", href: "/about" },
  { name: "Technology", href: "/technology" },
  { name: "Products", href: "/products" },
  { name: "Safety", href: "/safety" },
  { name: "Careers", href: "/careers" },
  { name: "Contact", href: "/contact" }
];

export const footerLinks = {
  company: [
    { name: "About Us", href: "#company" },
    { name: "Careers", href: "#careers" },
    { name: "Press", href: "#" }
  ],
  products: [
    { name: "Traction Battery", href: "#products" },
    { name: "Energy Storage", href: "#products" },
    { name: "Starter Battery", href: "#products" }
  ],
  solutions: [
    { name: "Material Handling", href: "#solutions" },
    { name: "Backup Power", href: "#solutions" },
    { name: "Industrial & Grid", href: "#solutions" },
    { name: "Starter Batteries", href: "#solutions" }
  ],
  resources: [
    { name: "Product Briefs", href: "#" },
    { name: "Datasheets", href: "#" },
    { name: "Case Studies", href: "#" }
  ]
};

export const socialLinks = {
  twitter: "https://x.com/GegadyneEnergy",
  linkedin: "https://in.linkedin.com/company/gegadyne"
};

export const companyAddress = {
  full: "Gegadyne Energy Pvt. Ltd., Plot No. C-35, TTC Industrial Area, Turbhe MIDC, Navi Mumbai, Maharashtra 400705",
  short: "Turbhe MIDC, Navi Mumbai",
  mapUrl: "https://maps.app.goo.gl/8x5CBoJ3EDMcb3Ej6",
  mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.5!2d72.8751!3d19.1254!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9c676018b43%3A0x75f29a4205098f99!2sL%20P%20House%2C%20Central%20Rd%2C%20SEEPZ%2C%20Andheri%20East%2C%20Mumbai%2C%20Maharashtra%20400093!5e0!3m2!1sen!2sin!4v1706000000000!5m2!1sen!2sin"
};

export const careersContent = {
  title: "Join the Energy Revolution",
  subtitle: "We're building the future of battery technology. Come build it with us.",
  openings: [
    {
      id: 1,
      title: "Battery Research Scientist",
      department: "R&D",
      location: "Mumbai",
      type: "Full-time"
    },
    {
      id: 2,
      title: "Electrochemistry Engineer",
      department: "Engineering",
      location: "Mumbai",
      type: "Full-time"
    },
    {
      id: 3,
      title: "Product Manager - Energy Storage",
      department: "Product",
      location: "Mumbai",
      type: "Full-time"
    },
    {
      id: 4,
      title: "Manufacturing Engineer",
      department: "Operations",
      location: "Mumbai",
      type: "Full-time"
    }
  ]
};

export const contactInfo = {
  title: "Talk to Our Engineering Team",
  subtitle: "Discuss your application requirements and get technical specifications for your use case.",
  email: "info@gegadyne.com",
  phone: "+91-0226988100",
  address: "Gegadyne Energy House, Opp Seepz Signal, MIDC Central Rd, Andheri East, Mumbai, Maharashtra 400093",
  fullAddress: "Gegadyne Energy House, Opp Seepz Signal, MIDC Central Rd, Andheri East, Mumbai, Maharashtra 400093",
  mapUrl: "https://maps.app.goo.gl/8x5CBoJ3EDMcb3Ej6"
};

export const whyNonLithium = {
  title: "Why Non-Lithium?",
  subtitle: "The case for Multi-Ion technology",
  points: [
    {
      id: 1,
      icon: "Globe",
      title: "Energy Independence",
      stat: "70%",
      statLabel: "of lithium processing concentrated in one region",
      description: "Sodium and potassium are globally abundant. No single country controls supply, eliminating geopolitical risk.",
      multiIon: "Abundant globally"
    },
    {
      id: 2,
      icon: "TrendingDown",
      title: "Cost Stability",
      stat: "13x",
      statLabel: "lithium price volatility in 3 years",
      description: "Multi-Ion raw materials have stable, predictable pricing. No exposure to lithium market speculation.",
      multiIon: "Stable pricing"
    },
    {
      id: 3,
      icon: "Shield",
      title: "Inherent Safety",
      stat: "Zero",
      statLabel: "safety incidents",
      description: "Our chemistry is inherently stable. No dendrite formation means no internal shorts and enhanced safety.",
      multiIon: "Inherently stable"
    },
    {
      id: 4,
      icon: "Zap",
      title: "Superior Performance",
      stat: "2.5x",
      statLabel: "longer cycle life",
      description: "5,000+ cycles vs ~2,000 for lithium. Operates from -40°C to 65°C where lithium fails.",
      multiIon: "5,000+ cycles"
    }
  ]
};

export const whyMultiIon = {
  title: "Multi-Ion vs Lithium-Ion",
  subtitle: "A direct comparison of key performance metrics for industrial applications",
  comparisons: [
    {
      metric: "Charge Time",
      lithium: "4-8 hours",
      multiIon: "15 minutes",
      outcome: "24/7 operations without battery swaps"
    },
    {
      metric: "Cycle Life",
      lithium: "1,500-2,000 cycles",
      multiIon: "5,000+ cycles",
      outcome: "3x longer battery replacement intervals"
    },
    {
      metric: "Temperature Range",
      lithium: "0°C to 45°C",
      multiIon: "-40°C to 65°C",
      outcome: "Works in freezers and extreme heat"
    },
    {
      metric: "Safety",
      lithium: "Requires safety systems",
      multiIon: "Inherently stable",
      outcome: "No special safety systems needed"
    },
    {
      metric: "Total Cost of Ownership",
      lithium: "Higher (replacements + downtime)",
      multiIon: "40% lower TCO",
      outcome: "Significant operational savings"
    }
  ]
};
